import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import {
  assignAsset, deleteAsset, getAsset, getAssetArc, getAssetDefender, getAssetStatusHistory, linkAsset, refreshWarranty, returnAsset, setAssetHeldBy, setAssetServices, unlinkAsset, updateAsset,
  type AssignBody,
} from '@/api/assets';
import { getServices, getServiceEnvironments } from '@/api/services';
import { lifecycleStates } from '../schema';
import { actionLabel, auditChanges } from './auditDiff';
import { LocationSelect, PersonPicker, pickerInputCls } from '@/components/common/assignPickers';
import { getAudit } from '@/api/audit';
import { getLocations } from '@/api/locations';
import { getFleet } from '@/api/reference';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { WarrantyExpiry } from '@/components/ui/WarrantyExpiry';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { isActiveDevice, isPrimaryUserMismatch, primaryUserVsAssignee } from '@/lib/deviceActivity';
import { AssetTimelineTab } from './AssetTimelineTab';
import { AssetArcTab, ArcBadges } from './AssetArcTab';
import { AssetDefenderTab, DefenderBadges } from './AssetDefenderTab';
import type { AssetAttributes, AssetLink, AssetServiceLink } from '@/types/oscar';

type Tab = 'profile' | 'assignment' | 'links' | 'services' | 'intune' | 'arc' | 'defender' | 'warranty' | 'trail' | 'history' | 'audit';

export function AssetDetail() {
  const { id } = useParams();
  const assetId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canMutate, isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('profile');
  const [confirm, setConfirm] = useState<null | 'return' | 'delete' | 'assign' | 'link' | 'heldBy'>(null);
  const [assignKind, setAssignKind] = useState<AssignBody['AssigneeKind']>('Person');
  const [assignValue, setAssignValue] = useState('');
  const [assignPrefillName, setAssignPrefillName] = useState<string | undefined>(undefined);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [childId, setChildId] = useState('');
  // Combined-assign extras (meeting 26/06): set status + device location + a note while assigning.
  const [assignStatus, setAssignStatus] = useState('');
  const [assignDeviceLoc, setAssignDeviceLoc] = useState('');
  const [assignNote, setAssignNote] = useState('');
  // Held by (meeting 20/08/2026): who is responsible for an asset that sits at a
  // location but belongs to no individual. Set while assigning, and changeable on
  // its own at handover without closing the assignment.
  const [assignHeldBy, setAssignHeldBy] = useState('');
  const [heldByValue, setHeldByValue] = useState('');
  const [heldByError, setHeldByError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['asset', assetId], queryFn: ({ signal }) => getAsset(assetId, signal) });
  // ⚠ NOT FATAL. Most assets are not Arc servers, and until the Arc SQL has run the API says `Arc: null`: the tab simply is not there.
  const arcQuery = useQuery({ queryKey: ['asset-arc', assetId], queryFn: ({ signal }) => getAssetArc(assetId, signal), retry: false });
  // ⚠ NOT FATAL either. Only Intune-enrolled Windows devices have Defender data; until the Defender SQL has run the API says `Defender: null`.
  const defenderQuery = useQuery({ queryKey: ['asset-defender', assetId], queryFn: ({ signal }) => getAssetDefender(assetId, signal), retry: false });
  // Resolve the device's location id → name (cache shared with the assign dialog).
  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), retry: false });
  const locationName = new Map((locationsQuery.data ?? []).map((l) => [l.LocationId, l.LocationDesc] as const));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['asset', assetId] });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };
  const mAssign = useMutation({
    mutationFn: (b: AssignBody) => assignAsset(assetId, b),
    onSuccess: () => { invalidate(); setConfirm(null); setAssignValue(''); setAssignError(null); },
    onError: (err) => setAssignError(err instanceof ApiError ? err.message : 'Could not assign the asset.'),
  });
  const mReturn = useMutation({ mutationFn: () => returnAsset(assetId), onSuccess: invalidate });
  // Wiped / locked in Intune (mig 066) — Helpdesk ticks these by hand; the date is
  // stamped server-side on the transition, so there is nothing to type.
  const [intuneFlagError, setIntuneFlagError] = useState<string | null>(null);
  const mIntuneFlag = useMutation({
    mutationFn: (body: { WipedInIntune?: boolean; LockedInIntune?: boolean }) => updateAsset(assetId, body),
    onSuccess: () => { invalidate(); setIntuneFlagError(null); },
    onError: (err) => setIntuneFlagError(err instanceof ApiError ? err.message : 'Could not save that.'),
  });
  const mHeldBy = useMutation({
    mutationFn: (personId: number | null) => setAssetHeldBy(assetId, personId),
    onSuccess: () => { invalidate(); setConfirm(null); setHeldByValue(''); setHeldByError(null); },
    onError: (err) => setHeldByError(err instanceof ApiError ? err.message : 'Could not set the responsible person.'),
  });
  const mRefresh = useMutation({ mutationFn: () => refreshWarranty(assetId), onSuccess: invalidate });
  const mDelete = useMutation({ mutationFn: () => deleteAsset(assetId), onSuccess: () => { invalidate(); navigate('/assets'); } });
  const mLink = useMutation({ mutationFn: (child: number) => linkAsset(assetId, { ChildAssetId: child }), onSuccess: () => { invalidate(); setChildId(''); } });
  const mUnlink = useMutation({ mutationFn: (linkId: number) => unlinkAsset(assetId, linkId), onSuccess: invalidate });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load this asset.</p>;

  const { Asset: a, CurrentAssignment: current, History: history, Links: links } = query.data;
  const allocations = query.data.RequestAllocations ?? [];
  // Match all Dell make variants (Dell, Dell Inc., Dell Pro …) — mirrors the
  // sync's WHERE Make LIKE 'Dell%'.
  const dell = (a.Make ?? '').trim().toLowerCase().startsWith('dell');
  const isSim = a.Category === 'SIM';
  // Top bar leads with the hostname (the recognisable name for servers/devices),
  // falling back to serial / mobile number. The serial is shown alongside below.
  const title = a.Hostname || a.SerialNumber || a.Msisdn || `Asset #${a.AssetId}`;
  const showSerialInSubtitle = !!a.Hostname && !!a.SerialNumber;
  // Who/what the asset is currently with (person, location or fleet item), from
  // the open assignment — surfaced next to the status in the overview line.
  const assignee = current ? (current.PersonName ?? current.LocationName ?? current.FleetItemId ?? null) : null;
  const locationLabel = a.LocationId != null ? (locationName.get(a.LocationId) ?? `#${a.LocationId}`) : null;
  // Same place, said twice — see the Profile rows below.
  const assignedToOwnLocation =
    current?.AssigneeKind === 'Location' && current.LocationId != null && current.LocationId === a.LocationId;

  const openAssign = () => {
    setAssignPrefillName(undefined);
    setAssignError(null);
    setAssignStatus(a.LifecycleState);
    setAssignDeviceLoc(a.LocationId != null ? String(a.LocationId) : '');
    // Marty, 01/09/2026: show the CURRENT notes here so they can be edited or added
    // to in the same step, rather than having to Assign and then Edit. Seeded fresh
    // each time the dialog opens so it can never post a stale snapshot.
    setAssignNote(a.Notes ?? '');
    setAssignHeldBy('');
    setConfirm('assign');
  };
  // ⛔ INTUNE KNOWS WHO USES IT, OSCAR DOES NOT (Anthony, 01/11/2026: his own laptop, primary user him, no hint it
  // could be assigned to him). Offered, never done for them: the button opens the Assign dialog with that person
  // already chosen, and a person confirms it.
  const intunePerson = query.data.IntunePrimaryPerson ?? null;
  const suggestIntuneAssignee = !!a.IntuneDeviceId && !!intunePerson && !current;
  const assignToIntuneUser = () => {
    if (!intunePerson) return;
    openAssign();
    setAssignKind('Person');
    setAssignValue(String(intunePerson.PersonId));
    setAssignPrefillName(intunePerson.DisplayName ?? a.IntuneAssignedUpn ?? 'Intune primary user');
  };
  const submitAssign = () => {
    setAssignError(null);
    if (!assignValue) {
      setAssignError(assignKind === 'Fleet' ? 'Enter a fleet item ID.' : `Select a ${assignKind.toLowerCase()}.`);
      return;
    }
    const body: AssignBody = { AssigneeKind: assignKind };
    if (assignKind === 'Person') body.PersonId = Number(assignValue) || undefined;
    if (assignKind === 'Location') body.LocationId = Number(assignValue) || undefined;
    if (assignKind === 'Fleet') body.FleetItemId = assignValue || undefined;
    if (assignKind === 'Location' && assignHeldBy) body.HeldByPersonId = Number(assignHeldBy) || undefined;
    if (assignStatus) body.Status = assignStatus;
    if (assignDeviceLoc) body.AssetLocationId = Number(assignDeviceLoc) || undefined;
    if (assignNote !== (a.Notes ?? '')) body.NotesText = assignNote;
    mAssign.mutate(body);
  };

  const tabs: [Tab, string][] = [
    ['profile', 'Profile'],
    ['assignment', 'Assignment history'],
    ['links', 'Links'],
    ['services', 'Services'],
    ['intune', 'Intune'],
    ...(arcQuery.data?.Arc ? ([['arc', 'Azure Arc']] as [Tab, string][]) : []),
    ...(defenderQuery.data?.Defender ? ([['defender', 'Defender']] as [Tab, string][]) : []),
    ['warranty', 'Warranty'],
    ['trail', 'Location & user history'],
    ['history', 'Status history'],
    ['audit', 'Audit'],
  ];

  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/assets" className="text-xs text-navy hover:underline">← Assets</Link>
          <h1 className="font-heading text-2xl font-bold text-navy">{title}</h1>
          {arcQuery.data?.Arc && <ArcBadges arc={arcQuery.data.Arc} />}
          {defenderQuery.data?.Defender && <DefenderBadges defender={defenderQuery.data.Defender} />}
          <p className="text-sm text-darkGrey/80">{showSerialInSubtitle ? <>{a.SerialNumber} · </> : null}{[a.Make, a.Model].filter(Boolean).join(' ') || a.Category} · {a.LifecycleState}{assignee ? <> · {assignee}</> : null}</p>
        </div>
        {canMutate && (
          <div className="flex flex-wrap gap-2">
            <Link to={`/assets/${assetId}/edit`} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit</Link>
            <button type="button" onClick={openAssign} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Assign</button>
            {current?.AssigneeKind === 'Location' && (
              <button type="button" onClick={() => { setHeldByValue(''); setHeldByError(null); setConfirm('heldBy'); }} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Held by</button>
            )}
            <button type="button" onClick={() => setConfirm('return')} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Return</button>
            {isAdmin && <button type="button" onClick={() => setConfirm('delete')} className="rounded border border-red/40 bg-white px-3 py-1.5 text-sm font-medium text-red hover:bg-red/5">Delete</button>}
          </div>
        )}
      </div>

      {suggestIntuneAssignee && intunePerson && (
        <div role="status" className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded border border-navy/30 bg-navy/5 px-3 py-2 text-sm text-darkGrey">
          <span>
            <strong className="font-semibold text-navy">Intune has {intunePerson.DisplayName ?? a.IntuneAssignedUpn} as the primary user</strong>, but this is not assigned to anyone in Oscar.
          </span>
          {canMutate && (
            <button type="button" onClick={assignToIntuneUser} className="rounded border border-navy bg-white px-3 py-1 text-sm font-semibold text-navy hover:bg-bg">
              Assign to {intunePerson.DisplayName ?? 'them'}
            </button>
          )}
        </div>
      )}

      <div className="mb-4 flex gap-1 border-b border-border text-sm">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={tab === k ? 'border-b-2 border-orange px-3 py-2 font-semibold text-navy' : 'px-3 py-2 text-darkGrey/70 hover:text-navy'}>{l}</button>
        ))}
      </div>

      {tab === 'profile' && (
        <>
          <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
            <Row label="Asset tag" value={a.AssetTag} />
            <Row label="Category" value={a.Category} />
            {/* Hostname kept in the left column (before Serial) for servers/devices. */}
            {!isSim && <Row label="Hostname" value={a.Hostname} />}
            <Row label={isSim ? 'ICCID' : 'Serial number'} value={a.SerialNumber} />
            {isSim && <Row label="Mobile number" value={a.Msisdn} />}
            <Row label="Supplier" value={a.Supplier} />
            <Row label="Purchase date" value={formatDate(a.PurchaseDate)} />
            <Row label="Warranty expiry" value={formatDate(a.WarrantyExpiry)} />
            {allocations.length > 0 && (
              <p role="status" className="rounded border border-navy/20 bg-[#E6ECF2] px-3 py-2 text-sm text-darkGrey">
                {allocations.map((x) => (
                  <span key={x.FulfilmentId} className="block">
                    Allocated to hardware request #{x.SubmissionId}{x.RequestFor ? ` for ${x.RequestFor}` : ''}.
                    {' '}Assigning it to them here issues it on the request.
                  </span>
                ))}
              </p>
            )}
            <Row label="Status" value={a.LifecycleState} />
            {/* Marty, 01/09/2026: "Only require 1 Location — remove one of them".
                For an asset assigned to the location it already sits at, Location and
                Assigned to held the same value and read as a duplicate. They are still
                different things, so they collapse into one row only when they AGREE —
                when they differ, that disagreement is worth seeing. */}
            {assignedToOwnLocation ? (
              <Row label="Location" value={`${locationLabel ?? ''} · assigned here`} />
            ) : (
              <>
                <Row label="Location" value={locationLabel} />
                <Row label="Assigned to" value={assigneeText(current)} />
              </>
            )}
            {current?.AssigneeKind === 'Location' && <Row label="Held by" value={current.HeldByPersonName} />}
            <Row label="Previously held by" value={a.PriorUser} />
            <Row label="Wiped in Intune" value={a.WipedInIntune ? `Yes — ${formatDateTime(a.WipedInIntuneDate)}` : 'No'} />
            <Row label="Locked in Intune" value={a.LockedInIntune ? `Yes — ${formatDateTime(a.LockedInIntuneDate)}` : 'No'} />
            {nonMigrationAttributes(a.Attributes).map(([k, v]) => (
              <Row key={k} label={k} value={v == null ? null : String(v)} />
            ))}
            <Row label="Notes" value={a.Notes} />
          </dl>
          {migrationAttributes(a.Attributes).length > 0 && (
            <details className="mt-5 rounded border border-border bg-bg/40 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Migration reference (BlueTally)</summary>
              <dl className="mt-3 grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
                {migrationAttributes(a.Attributes).map(([k, v]) => (
                  <Row key={k} label={migrationLabel(k)} value={v == null ? null : String(v)} />
                ))}
              </dl>
            </details>
          )}
        </>
      )}

      {tab === 'assignment' && (
        <>
          <p className="mb-2 text-xs text-slate-600">
            Who has held this asset, newest first. Held-by changes within one assignment don't appear here —
            they're on the <button type="button" onClick={() => setTab('audit')} className="font-semibold text-navy underline">Audit</button> tab.
          </p>
          <ul className="space-y-2 text-sm">
            {history.length === 0 && <li className="text-darkGrey/70">No assignment history.</li>}
            {history.map((as) => (
              <li key={as.AssignmentId} className="rounded border border-border bg-white px-3 py-2">
                <span className="font-semibold text-navy">{assigneeText(as)}</span>
                {as.ReturnedDate === null && (
                  <span className="ml-2 inline-flex rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">Current</span>
                )}
                {as.HeldByPersonName && <span className="text-darkGrey/70"> · held by {as.HeldByPersonName}</span>}
                <p className="mt-0.5 text-xs text-slate-600">
                  {formatDateTime(as.AssignedDate)} → {as.ReturnedDate ? formatDateTime(as.ReturnedDate) : 'current'}
                  {as.CreatedBy && <> · assigned by {as.CreatedBy}</>}
                  {as.ReturnedDate && as.ModifiedBy && <> · returned by {as.ModifiedBy}</>}
                </p>
                {as.Notes && <p className="mt-0.5 text-xs text-darkGrey/70">{as.Notes}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {tab === 'links' && (
        <div className="space-y-3">
          <p className="text-xs text-darkGrey/70">SIM-in-handset links. Link a SIM (child) into this handset, or this SIM into a handset — the prior live link is closed automatically.</p>
          <ul className="space-y-2 text-sm">
            {links.length === 0 && <li className="text-darkGrey/70">No active links.</li>}
            {links.map((l) => (
              <li key={l.AssetLinkId} className="flex items-center justify-between rounded border border-border bg-white px-3 py-2">
                <span>{linkText(l, assetId)} <span className="text-darkGrey/60">· since {formatDate(l.LinkedDate)}</span></span>
                {canMutate && <button type="button" onClick={() => mUnlink.mutate(l.AssetLinkId)} className="text-xs font-semibold text-red hover:underline">Unlink</button>}
              </li>
            ))}
          </ul>
          {canMutate && (
            <div className="flex items-end gap-2">
              <label className="text-xs font-semibold text-darkGrey">
                Child asset ID (the SIM)
                <input value={childId} onChange={(e) => setChildId(e.target.value)} placeholder="Asset ID" className="mt-1 block rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
              </label>
              <button type="button" disabled={!childId || mLink.isPending} onClick={() => mLink.mutate(Number(childId))} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">Link</button>
            </div>
          )}
        </div>
      )}

      {tab === 'intune' && (
        <div className="mb-4 rounded border border-border bg-white p-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Recorded by Helpdesk</h2>
          <p className="mt-1 text-xs text-slate-600">
            Ticked by hand — Intune does not report these back to Oscar. The date is recorded automatically.
          </p>
          {intuneFlagError && <div role="alert" className="mt-2 rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{intuneFlagError}</div>}
          <div className="mt-2 space-y-2">
            <IntuneActionToggle
              label="Device wiped in Intune"
              checked={a.WipedInIntune}
              at={a.WipedInIntuneDate}
              disabled={!canMutate || mIntuneFlag.isPending}
              onChange={(v) => mIntuneFlag.mutate({ WipedInIntune: v })}
            />
            <IntuneActionToggle
              label="Device locked in Intune"
              checked={a.LockedInIntune}
              at={a.LockedInIntuneDate}
              disabled={!canMutate || mIntuneFlag.isPending}
              onChange={(v) => mIntuneFlag.mutate({ LockedInIntune: v })}
            />
          </div>
        </div>
      )}

      {tab === 'intune' && (
        a.IntuneStatus == null ? (
          <p className="text-sm text-darkGrey/70">This asset has not been reconciled with Intune yet.</p>
        ) : (
          <div className="space-y-3">
            {a.IntuneDeviceId && (
              <a
                href={intunePortalUrl(a.IntuneDeviceId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg"
              >
                <ExternalLink className="h-4 w-4" aria-hidden /> Open in Intune
              </a>
            )}
            {/* ⛔ THE PRIMARY USER WAS NEVER UPDATED AFTER ASSIGNMENT (Marty, 29/10/2026). Said where the
                person fixing it is looking, with the one action that fixes it: Intune is the source for the
                primary user, so Oscar cannot change it - and TeamViewer does not show the device against
                anybody until it is. */}
            {(() => {
              const state = primaryUserVsAssignee(a);
              if (state !== 'none' && state !== 'differs') return null;
              return (
                <div role="alert" className="flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
                  <span>
                    <strong className="font-semibold text-navy">The Intune primary user is not the person this is assigned to.</strong>{' '}
                    {state === 'none'
                      ? `Oscar has it assigned to ${a.AssignedPersonName ?? a.AssignedPersonEmail}, but Intune has no primary user.`
                      : `Oscar has it assigned to ${a.AssignedPersonName ?? a.AssignedPersonEmail}, but the Intune primary user is ${a.IntuneAssignedUpn}.`}{' '}
                    Set the primary user in Intune so the device shows against them there and in TeamViewer.
                  </span>
                </div>
              );
            })()}
            {isPrimaryUserMismatch(a) && (
              <div role="alert" className="flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
                <span><strong className="font-semibold text-navy">Primary user may be incorrect.</strong> Last signed in by <strong>{a.IntuneLastLogonUpn}</strong>, but the Intune primary user is <strong>{a.IntuneAssignedUpn}</strong>.</span>
              </div>
            )}
            <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
              {/* Mirrors the Intune portal "Essentials" panel. */}
              <Row label="Intune status" value={a.IntuneStatus} />
              <Row label="Compliance" value={a.IntuneCompliance} />
              <Row label="Device name" value={a.IntuneDeviceName} />
              <Row label="Management name" value={a.IntuneManagedDeviceName} />
              <Row label="Ownership" value={a.IntuneOwnership} />
              <Row label="Primary user" value={a.IntuneAssignedUpn} />
              <Row label="Assigned to (Oscar)" value={a.AssignedPersonName ?? null} />
              <Row label="Last signed in by" value={a.IntuneLastLogonUpn} />
              <Row label="Last signed in" value={a.IntuneLastLogonDate ? formatDateTime(a.IntuneLastLogonDate) : null} />
              <div className="flex gap-2">
                <dt className="w-40 shrink-0 font-semibold text-darkGrey">Activity</dt>
                <dd><ActivityBadge value={a.IntuneLastLogonDate} /></dd>
              </div>
              <Row label="Serial number" value={a.SerialNumber} />
              {/* Prefer the IMEI synced from Intune; fall back to the manually-entered Attributes value. */}
              <Row label="IMEI" value={a.IntuneImei ?? attrText(a.Attributes, 'IMEI')} />
              <Row label="IMEI (2)" value={attrText(a.Attributes, 'IMEI2')} />
              <Row label="Phone number" value={a.IntunePhoneNumber} />
              <Row label="Manufacturer" value={a.IntuneManufacturer} />
              <Row label="Model" value={a.IntuneModel} />
              <Row label="Operating system" value={[a.IntuneOs, a.IntuneOsVersion].filter(Boolean).join(' ')} />
              <Row label="Intune device ID" value={a.IntuneDeviceId} />
              <Row label="Enrolled" value={a.IntuneEnrolledDate ? formatDateTime(a.IntuneEnrolledDate) : null} />
              <Row label="Last check-in" value={a.IntuneLastCheckIn ? formatDateTime(a.IntuneLastCheckIn) : null} />
              <Row label="Last sync" value={a.IntuneLastSyncDate ? formatDateTime(a.IntuneLastSyncDate) : null} />
            </dl>
          </div>
        )
      )}

      {tab === 'services' && (
        <ServicesTab
          assetId={assetId}
          current={query.data.Services ?? []}
          canMutate={canMutate}
          onSaved={invalidate}
        />
      )}

      {tab === 'warranty' && (
        <div>
          {canMutate && (
            <div className="mb-3">
              <button
                type="button"
                disabled={!dell || mRefresh.isPending}
                title={dell ? undefined : 'Only available for Dell assets'}
                onClick={() => mRefresh.mutate()}
                className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                {mRefresh.isPending ? 'Refreshing…' : 'Refresh from Dell'}
              </button>
              {!dell && <span className="ml-2 text-xs text-darkGrey/60">Dell assets only.</span>}
            </div>
          )}
          <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
            <Row label="Service level" value={a.DellServiceLevel} />
            <Row label="Warranty status" value={a.DellWarrantyStatus} />
            <div className="flex gap-2">
              <dt className="w-40 shrink-0 font-semibold text-darkGrey">Warranty expiry</dt>
              <dd><WarrantyExpiry value={a.WarrantyExpiry} /></dd>
            </div>
            <Row label="Last sync" value={a.DellWarrantyLastSyncDate ? formatDateTime(a.DellWarrantyLastSyncDate) : null} />
            <Row label="Last sync error" value={a.DellWarrantyLastSyncError} />
          </dl>
        </div>
      )}

      {tab === 'arc' && arcQuery.data?.Arc && <AssetArcTab arc={arcQuery.data.Arc} />}
      {tab === 'defender' && defenderQuery.data?.Defender && <AssetDefenderTab defender={defenderQuery.data.Defender} />}
      {tab === 'trail' && <AssetTimelineTab assetId={assetId} />}
      {tab === 'history' && <StatusHistoryTab assetId={assetId} />}
      {tab === 'audit' && <AuditTab assetId={assetId} />}

      <AlertDialog
        open={confirm === 'assign'}
        onOpenChange={(o) => { setConfirm(o ? 'assign' : null); if (!o) { setAssignValue(''); setAssignPrefillName(undefined); setAssignError(null); } }}
        title="Assign asset"
        confirmLabel="Assign"
        closeOnConfirm={false}
        onConfirm={submitAssign}
      >
        <div className="space-y-2">
          {assignError && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{assignError}</div>}
          <label className="block text-xs font-semibold text-darkGrey">Assign to
            <select aria-label="Assignee kind" value={assignKind} onChange={(e) => { setAssignKind(e.target.value as AssignBody['AssigneeKind']); setAssignValue(''); setAssignError(null); }} className="mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
              <option value="Person">Person</option>
              <option value="Location">Location</option>
              <option value="Fleet">Fleet</option>
            </select>
          </label>
          {assignKind === 'Person' && <PersonPicker value={assignValue} onChange={setAssignValue} initialName={assignPrefillName} />}
          {assignKind === 'Location' && <LocationSelect value={assignValue} onChange={setAssignValue} />}
          {assignKind === 'Location' && (
            <label className="block text-xs font-semibold text-darkGrey">Held by (responsible person, optional)
              <PersonPicker value={assignHeldBy} onChange={setAssignHeldBy} />
            </label>
          )}
          {assignKind === 'Fleet' && <AssignFleetPicker value={assignValue} onChange={setAssignValue} />}
          <label className="block text-xs font-semibold text-darkGrey">Status
            <select value={assignStatus} onChange={(e) => setAssignStatus(e.target.value)} className="mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
              {lifecycleStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Device location
            <LocationSelect value={assignDeviceLoc} onChange={setAssignDeviceLoc} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Notes
            <textarea
              aria-label="Notes"
              value={assignNote}
              onChange={(e) => setAssignNote(e.target.value)}
              rows={4}
              className="mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
            />
            <span className="mt-1 block font-normal text-slate-600">
              The asset&apos;s current notes. Edit or add to them here — saving replaces
              what is stored, the same as Edit does. Clearing the box clears the notes.
            </span>
          </label>
        </div>
      </AlertDialog>

      <AlertDialog
        open={confirm === 'heldBy'}
        onOpenChange={(o) => { setConfirm(o ? 'heldBy' : null); if (!o) { setHeldByValue(''); setHeldByError(null); } }}
        title="Change who holds this asset"
        confirmLabel="Save"
        closeOnConfirm={false}
        onConfirm={() => { setHeldByError(null); mHeldBy.mutate(heldByValue ? Number(heldByValue) : null); }}
      >
        <div className="space-y-2">
          {heldByError && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{heldByError}</div>}
          <p className="text-xs text-darkGrey/70">
            Currently held by {current?.HeldByPersonName ?? 'nobody'}. The asset stays assigned to {current?.LocationName ?? 'the location'} —
            this only changes who is responsible for it. Pick the new holder, or save with nobody selected to clear it.
          </p>
          <PersonPicker value={heldByValue} onChange={setHeldByValue} />
        </div>
      </AlertDialog>

      <AlertDialog open={confirm === 'return'} onOpenChange={(o) => setConfirm(o ? 'return' : null)} title="Return asset?" description="Closes the current assignment (un-assigns the holder). Set the status separately if needed." confirmLabel="Return" onConfirm={() => mReturn.mutate()} />
      <AlertDialog open={confirm === 'delete'} onOpenChange={(o) => setConfirm(o ? 'delete' : null)} title="Delete this asset?" description={`${title} will be soft-deleted and hidden from the register.`} confirmLabel="Delete" destructive onConfirm={() => mDelete.mutate()} />
    </section>
  );
}


/** Search-as-you-type fleet picker over the cached insights.vwDimFleet list. */
function AssignFleetPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const fleet = useQuery({ queryKey: ['reference', 'fleet'], queryFn: ({ signal }) => getFleet(signal), retry: false });

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded bg-bg px-2 py-1 text-sm font-normal text-navy">Fleet {value}</span>
        <button type="button" onClick={() => onChange('')} className="text-xs font-normal text-red hover:underline">Clear</button>
      </div>
    );
  }

  const q = term.trim().toLowerCase();
  const matches = q.length === 0 ? [] : (fleet.data ?? [])
    .filter((f) => f.FleetItemId.toLowerCase().includes(q) || f.Description.toLowerCase().includes(q))
    .slice(0, 10);

  return (
    <div className="relative">
      <input
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        placeholder="Search fleet by number or description"
        aria-label="Fleet search"
        className={pickerInputCls}
      />
      {fleet.isError && <p className="mt-1 text-xs text-red">Fleet list unavailable (OSCAR_FLEET_VIEW not configured).</p>}
      {open && matches.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded border border-border bg-white text-sm shadow">
          {matches.map((f) => (
            <li key={f.FleetItemId}>
              <button
                type="button"
                onClick={() => { onChange(f.FleetItemId); setOpen(false); setTerm(''); }}
                className="block w-full px-3 py-1.5 text-left font-normal hover:bg-bg"
              >
                {f.FleetItemId}{f.Description ? <span className="text-darkGrey/60"> — {f.Description}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function assigneeText(a: { AssigneeKind: string; PersonId: number | null; LocationId: number | null; FleetItemId: string | null; PersonName?: string | null; LocationName?: string | null } | null): string | null {
  if (!a) return null;
  if (a.AssigneeKind === 'Person') return a.PersonName ?? `Person #${a.PersonId}`;
  if (a.AssigneeKind === 'Location') return a.LocationName ?? `Location #${a.LocationId}`;
  if (a.AssigneeKind === 'Fleet') return `Fleet ${a.FleetItemId}`;
  return a.AssigneeKind;
}

function linkText(l: AssetLink, selfId: number): string {
  // Show the "other" asset relative to the one being viewed.
  if (l.ParentAssetId === selfId) {
    return `Holds ${l.ChildCategory ?? 'asset'} ${l.ChildSerialNumber ?? l.ChildMsisdn ?? `#${l.ChildAssetId}`}`;
  }
  return `Installed in ${l.ParentCategory ?? 'asset'} ${l.ParentSerialNumber ?? `#${l.ParentAssetId}`}`;
}

/** One Helpdesk-recorded Intune action (mig 066): a tick plus when it was ticked. */
function IntuneActionToggle({
  label,
  checked,
  at,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  at: string | null;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-darkGrey">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span>{label}</span>
      {checked && at && <span className="text-xs text-slate-600">· {formatDateTime(at)}</span>}
    </label>
  );
}

/** Active/Inactive pill from the last interactive logon (migration 023). */
function ActivityBadge({ value }: { value: string | null }) {
  if (!value) return <span className="text-darkGrey/50">No sign-in data</span>;
  return isActiveDevice(value)
    ? <span className="inline-flex rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">Active</span>
    : <span className="inline-flex rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey">Inactive</span>;
}

/** Deep-link into the Intune admin centre for a managed device. */
function intunePortalUrl(deviceId: string): string {
  return `https://intune.microsoft.com/#view/Microsoft_Intune_Devices/DeviceSettingsMenuBlade/~/overview/mdmDeviceId/${encodeURIComponent(deviceId)}`;
}

/** Read one attribute as a display string (handles the JSON value union). */
function attrText(attrs: AssetAttributes | null, key: string): string | null {
  const v = attrs?.[key];
  return v == null || v === '' ? null : String(v);
}

// BlueTally migration-reference keys — surfaced in their own Profile section,
// kept out of the main attribute list (meeting 26/06).
const MIGRATION_KEYS = ['BtSourceId', 'BtAssetId', 'BtCategory', 'BtStatus', 'BtDepartment'];
const isMigrationKey = (k: string) => MIGRATION_KEYS.includes(k) || k.startsWith('Bt');

function nonMigrationAttributes(attrs: AssetAttributes | null): [string, string | number | boolean | null][] {
  return attrs ? Object.entries(attrs).filter(([k]) => !isMigrationKey(k)) : [];
}
function migrationAttributes(attrs: AssetAttributes | null): [string, string | number | boolean | null][] {
  return attrs ? Object.entries(attrs).filter(([k]) => isMigrationKey(k)) : [];
}
/** "BtDepartment" → "Department", "BtStatus" → "Status" for the migration panel. */
function migrationLabel(key: string): string {
  return key.startsWith('Bt') ? key.slice(2) : key;
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 font-semibold text-darkGrey">{label}</dt>
      <dd className="text-darkGrey/80">{value && value !== '—' ? value : '—'}</dd>
    </div>
  );
}

/**
 * Services tab (CMDB Slice 2) — the services this asset supports. Read view
 * shows the linked services; Helpdesk/Admin can edit the set from the shared
 * catalogue. This is what answers "decommission this server → which apps?".
 */
function ServicesTab({ assetId, current, canMutate, onSaved }: {
  assetId: number;
  current: AssetServiceLink[];
  canMutate: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [envBy, setEnvBy] = useState<Record<number, number | ''>>({});
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const catalogue = useQuery({
    queryKey: ['services'],
    queryFn: ({ signal }) => getServices(signal),
    enabled: editing,
    retry: false,
  });
  // Environments for each selected service (per-service environment dropdown).
  const envQueries = useQueries({
    queries: editing
      ? selected.map((sid) => ({
          queryKey: ['service-environments', sid],
          queryFn: ({ signal }: { signal: AbortSignal }) => getServiceEnvironments(sid, signal),
          staleTime: 60_000,
        }))
      : [],
  });

  const mutation = useMutation({
    mutationFn: () => setAssetServices(assetId, selected.map((sid) => ({ ServiceId: sid, EnvironmentId: envBy[sid] ? Number(envBy[sid]) : null }))),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save services.'),
  });

  const startEdit = () => {
    setSelected(current.map((s) => s.ServiceId));
    setEnvBy(Object.fromEntries(current.map((s) => [s.ServiceId, s.EnvironmentId ?? ''])));
    setError(null); setFilter(''); setEditing(true);
  };
  const toggle = (id: number) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const setEnv = (sid: number, v: number | '') => setEnvBy((prev) => ({ ...prev, [sid]: v }));

  if (!editing) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-darkGrey/70">Services this asset supports (from the shared CMDB catalogue). Used for change-impact analysis.</p>
        {current.length === 0 ? (
          <p className="text-sm text-darkGrey/70">No services linked.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {current.map((s) => (
              <li key={s.ServiceId} className="inline-flex items-center rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy">
                {s.Name}
                {s.EnvironmentName ? <span className="ml-1 font-normal text-navy/70">· {s.EnvironmentName}</span> : null}
                {s.ServiceGroup ? <span className="ml-1 font-normal text-darkGrey/60">· {s.ServiceGroup}</span> : null}
              </li>
            ))}
          </ul>
        )}
        {canMutate && (
          <button type="button" onClick={startEdit} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit services</button>
        )}
      </div>
    );
  }

  const allCat = catalogue.data ?? [];
  const selectedSet = new Set(selected);
  // Only active services (keep an already-linked inactive one so it can be removed).
  const active = allCat.filter((s) => s.IsActive || selectedSet.has(s.ServiceId));
  const q = filter.trim().toLowerCase();
  const visible = q ? active.filter((s) => s.Name.toLowerCase().includes(q) || (s.ServiceGroup ?? '').toLowerCase().includes(q)) : active;
  // Group the catalogue list by Service Group (alphabetical; "Ungrouped" last).
  const grouped = (() => {
    const m = new Map<string, typeof visible>();
    for (const s of visible) { const k = s.ServiceGroup || 'Ungrouped'; (m.get(k) ?? m.set(k, []).get(k)!).push(s); }
    return [...m.entries()].sort((a, b) => a[0] === 'Ungrouped' ? 1 : b[0] === 'Ungrouped' ? -1 : a[0].localeCompare(b[0]));
  })();
  // Selected services that have environments → show a per-service environment select.
  const envRows = selected
    .map((sid, i) => ({ sid, envs: envQueries[i]?.data ?? [] }))
    .filter((r) => r.envs.length > 0);
  const nameOf = (sid: number) => allCat.find((s) => s.ServiceId === sid)?.Name ?? current.find((s) => s.ServiceId === sid)?.Name ?? `Service #${sid}`;

  return (
    <div className="space-y-2">
      {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter services…"
        aria-label="Filter services"
        className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm"
      />
      <div className="max-h-60 overflow-y-auto rounded border border-border">
        {catalogue.isLoading && <p className="px-3 py-2 text-xs text-darkGrey/60">Loading catalogue…</p>}
        {catalogue.isError && <p className="px-3 py-2 text-xs text-red">Could not load the service catalogue.</p>}
        {!catalogue.isLoading && !catalogue.isError && visible.length === 0 && <p className="px-3 py-2 text-xs text-darkGrey/60">No services match.</p>}
        {grouped.map(([group, items]) => (
          <div key={group}>
            <div className="sticky top-0 bg-bg px-3 py-1 text-[0.62rem] font-bold uppercase tracking-wide text-darkGrey/60">{group}</div>
            {items.map((s) => (
              <label key={s.ServiceId} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-bg">
                <input type="checkbox" checked={selectedSet.has(s.ServiceId)} onChange={() => toggle(s.ServiceId)} />
                <span>{s.Name}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
      {envRows.length > 0 && (
        <div className="rounded border border-border bg-bg/40 p-2 space-y-1.5">
          <span className="block text-xs font-semibold text-darkGrey/70">Environment (optional)</span>
          {envRows.map(({ sid, envs }) => (
            <div key={sid} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-darkGrey/90">{nameOf(sid)}</span>
              <select
                value={envBy[sid] ?? ''}
                onChange={(e) => setEnv(sid, e.target.value === '' ? '' : Number(e.target.value))}
                aria-label={`Environment for ${nameOf(sid)}`}
                className="shrink-0 rounded border border-border bg-white px-2 py-1 text-sm"
              >
                <option value="">Whole service</option>
                {envs.map((env) => <option key={env.EnvironmentId} value={env.EnvironmentId}>{env.Name}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" disabled={mutation.isPending} onClick={() => { setError(null); mutation.mutate(); }} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {mutation.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => setEditing(false)} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
      </div>
    </div>
  );
}

/** Full field-level trail. The Assignment history tab summarises WHO held the
 *  asset; this shows every change, including held-by handovers that update the
 *  open assignment in place and so leave no trace on the assignment row. */
function AuditTab({ assetId }: { assetId: number }) {
  const query = useQuery({ queryKey: ['audit', 'Asset', assetId], queryFn: ({ signal }) => getAudit({ entityType: 'Asset', entityId: assetId }, signal) });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load audit history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No audit history.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {query.data.map((e) => {
        const changes = auditChanges(e.OldValue, e.NewValue);
        return (
          <li key={e.AuditId} className="rounded border border-border bg-white px-3 py-2">
            <span className="font-semibold text-navy">{actionLabel(e.Action)}</span>
            <span className="text-darkGrey/70"> · {formatDateTime(e.ChangedDate)} · {e.ChangedBy}</span>
            {changes.length > 0 && (
              <dl className="mt-1.5 space-y-0.5 text-xs">
                {changes.map((c) => (
                  <div key={c.field} className="flex gap-2">
                    <dt className="w-36 shrink-0 font-semibold text-darkGrey">{c.field}</dt>
                    <dd className="text-darkGrey/80">
                      <span className="text-slate-600">{c.from}</span>
                      <span aria-hidden="true"> → </span>
                      <span className="sr-only"> changed to </span>
                      <span className="font-medium text-navy">{c.to}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {e.Notes && <p className="mt-1 text-xs text-darkGrey/70">{e.Notes}</p>}
          </li>
        );
      })}
    </ul>
  );
}

function StatusHistoryTab({ assetId }: { assetId: number }) {
  const query = useQuery({ queryKey: ['asset-status-history', assetId], queryFn: ({ signal }) => getAssetStatusHistory(assetId, signal) });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load status history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No status changes recorded.</p>;
  return (
    <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
          <tr>
            <th className="px-3 py-2">When</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Branch</th>
            <th className="px-3 py-2">Ticket</th>
            <th className="px-3 py-2">Prior user</th>
            <th className="px-3 py-2">By</th>
          </tr>
        </thead>
        <tbody>
          {query.data.map((h) => (
            <tr key={h.AssetStatusHistoryId} className="border-t border-border align-top">
              <td className="px-3 py-2 text-darkGrey/80 whitespace-nowrap">{formatDateTime(h.ChangedDate)}</td>
              <td className="px-3 py-2 font-medium text-navy">{h.Status}</td>
              <td className="px-3 py-2 text-darkGrey/80">{h.ContactName ?? '—'}</td>
              <td className="px-3 py-2 text-darkGrey/80">{h.Branch ?? '—'}</td>
              <td className="px-3 py-2 text-darkGrey/80">{h.TicketId ?? '—'}</td>
              <td className="px-3 py-2 text-darkGrey/80">{h.PriorUser ?? '—'}</td>
              <td className="px-3 py-2 text-darkGrey/70">{h.ChangedBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
